import { NEW_LOOK_RADIUS } from "common/NewLook/tokens.ts";
import {
  NEW_LOOK_PHONE_CONTROL,
  NEW_LOOK_PHONE_HIT,
  phoneMaxWidth,
} from "common/NewLook/layout";
import React from "react";
import useToggle from "@shared/common/Toggle/useToggle";
import { toggleStyles } from "@shared/common/Toggle/styles";
import styled, { css } from "styled-components";
import { SharedToggleProps } from "@shared/common/Toggle/types";
import { hexToRGB } from "@shared/utils/misc";
import { OverrideType } from "@shared/features/bots/BotForm/common/types";
import { translateOptionName } from "@shared/common/i18n/optionNames";

const OverrideTypeToggle = ({
  options,
  initialOption,
  onClick,
  disabled,
  style,
  showPersonalAsOn,
  selectedOption: propsSelectedOption,
  setDefaultValue,
}: SharedToggleProps<OverrideType> & { showPersonalAsOn?: boolean }) => {
  const { changeOption, selectedOption } = useToggle({
    options,
    initialOption,
    onClick,
    disabled,
    selectedOption: propsSelectedOption,
    setDefaultValue,
  });

  return (
    <Background $disabled={disabled} style={style}>
      {options?.map((option, i) => {
        const optionDisplay = translateOptionName(
          option === OverrideType.PERSONAL && showPersonalAsOn ? "On" : option
        );
        return (
          <Option
            key={i}
            onClick={() => changeOption?.(option)}
            $selected={selectedOption === option}
            $disabled={disabled}
            $isValueSelected={!!selectedOption}
            $overrideType={option}
          >
            {optionDisplay}
          </Option>
        );
      })}
    </Background>
  );
};

export default OverrideTypeToggle;

/**
 * New look: a quiet segmented control. Off is neutral, the channel's value is
 * tinted and a personal value is the solid accent.
 */
const newLookOption = css<{ $selected: boolean; $overrideType: OverrideType }>`
  padding: 0 10px;
  border-radius: ${NEW_LOOK_RADIUS.chip}px;
  font-size: 12.5px;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  color: ${(p) => p.theme.colors.secondaryText};
  box-shadow: none;
  transition: color 0.2s, background 0.2s;
  &:hover {
    color: ${(p) => p.theme.colors.titleColor};
  }
  /* Phone: the option's hit area reaches 40px over the 32px track. */
  @media (max-width: ${phoneMaxWidth}) {
    position: relative;
    &::before {
      content: "";
      position: absolute;
      inset: -${(NEW_LOOK_PHONE_HIT - (NEW_LOOK_PHONE_CONTROL - 8)) / 2}px 0;
    }
  }
  ${(p) => {
    if (!p.$selected) return "";
    const isOff = [OverrideType.USER_CONFIGURATION, OverrideType.OFF].includes(
      p.$overrideType
    );
    const isChannel = [
      OverrideType.CHANNEL,
      OverrideType.ALL,
      OverrideType.DYNAMIC,
    ].includes(p.$overrideType);
    if (isOff) {
      return css`
        background: ${p.theme.colors.cardBgColor};
        color: ${p.theme.colors.titleColor};
        box-shadow: ${p.theme.shadows.dashboardCard},
          inset 0 0 0 1px ${p.theme.colors.cardBorderColor};
      `;
    }
    if (isChannel) {
      return css`
        background: ${p.theme.colors.lightBlue};
        color: ${p.theme.colors.primaryBlue};
        &:hover {
          color: ${p.theme.colors.primaryBlue};
        }
      `;
    }
    return css`
      background: ${p.theme.colors.primaryBlue};
      color: ${p.theme.colors.buttonText};
      &:hover {
        color: ${p.theme.colors.buttonText};
      }
    `;
  }};
  font-weight: ${(p) =>
    p.$selected
      ? p.theme.typography.fontWeights.SEMI_BOLD
      : p.theme.typography.fontWeights.MEDIUM};
`;

const Background = styled.div<{ $disabled?: boolean }>`
  ${toggleStyles.wrapper};
  width: fit-content;
  height: 27px;
  border-radius: 6px;
  background: ${(p) => hexToRGB(p.theme.colors.secondaryGrey, 0.08)};
  ${(p) =>
    p.$disabled &&
    `
    cursor: not-allowed;
  `};
  ${(p) =>
    p.theme.isNewLook &&
    css`
      height: 28px;
      gap: 2px;
      padding: 2px;
      border-radius: ${p.theme.radii.control}px;
      background: ${p.theme.colors.cardLightBgColor};
      box-shadow: inset 0 0 0 1px ${p.theme.colors.cardBorderColor};
      /* Phone: a 32px track (it sits on a title row) with a 40px hit area. */
      @media (max-width: ${phoneMaxWidth}) {
        height: ${NEW_LOOK_PHONE_CONTROL - 4}px;
      }
    `};
`;

const Option = styled.div<{
  $selected: boolean;
  $disabled?: boolean;
  $isValueSelected?: boolean;
  $overrideType: OverrideType;
}>`
  ${toggleStyles.option};
  white-space: nowrap;
  color: ${(p) => p.theme.colors.secondaryGrey};
  border-radius: 6px;
  padding: 4px 8px;
  cursor: pointer;
  ${(props) => {
    let bgColor: string | undefined;
    let color = props.theme.colors.buttonText;
    if (
      [OverrideType.USER_CONFIGURATION, OverrideType.OFF].includes(
        props.$overrideType
      )
    )
      bgColor = props.theme.colors.secondaryGrey;
    else if (
      [OverrideType.CHANNEL, OverrideType.ALL, OverrideType.DYNAMIC].includes(
        props.$overrideType
      )
    ) {
      bgColor = props.theme.isDarkMode
        ? props.theme.colors.lightBlue
        : props.theme.colors.blues[1];
      color = props.theme.colors.primaryBlue;
    } else if (
      props.$overrideType === OverrideType.PERSONAL ||
      props.$overrideType === OverrideType.CUSTOM
    )
      bgColor = props.theme.colors.primaryBlue;
    return (
      props.$selected &&
      `
    background-color: ${bgColor};
    color: ${color};
    box-shadow: 0px 6px 24px rgba(46, 73, 92, 0.123306);
  `
    );
  }}
  ${(p) =>
    p.$disabled &&
    `
    cursor: not-allowed;
  `};
  ${(p) => p.theme.isNewLook && newLookOption};
`;
